import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Building2, Info, Pencil, Plus } from "lucide-react";

import { disableAccount, getAccounts } from "src/api/accounts";
import useHasPermissions from "src/hooks/useHasPermissions";
import CustomModal from "src/components/CustomModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import NewAccountModal from "src/components/modals/NewAccountModal";
import PermissionWrapper from "src/components/PermissionWrapper";
import { Button } from "src/components/ui/button";
import { EmptyState, PageHead } from "src/components/common";
import { showSuccessToast } from "src/lib/utils";
import type { BankAccount } from "src/types/account";

const Accounts = () => {
	const { data } = useQuery({ queryKey: ["accounts"], queryFn: getAccounts, suspense: true });
	const accounts = data?.data ?? [];

	return (
		<div className="tw-space-y-5">
			<PageHead
				title="Bank Accounts"
				subtitle="Link the accounts your settlements are paid into."
				actions={
					<>
						<Link
							to="/disabled-accounts"
							className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-px-3 tw-py-2 tw-text-[12.5px] tw-font-semibold tw-text-text tw-transition-colors hover:tw-bg-bg"
						>
							Disabled Accounts
						</Link>
						<PermissionWrapper permission="add_bank_account">
							<CustomModal
								title="Add Bank Account"
								trigger={
									<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
										<Plus className="tw-h-4 tw-w-4" />
										Add Bank Account
									</Button>
								}
							>
								<NewAccountModal />
							</CustomModal>
						</PermissionWrapper>
					</>
				}
			/>

			<div className="tw-flex tw-items-start tw-gap-2.5 tw-rounded-lg tw-border tw-border-blue/20 tw-bg-blue-tint tw-p-3.5 tw-text-[13px] tw-text-text">
				<Info className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-blue" />
				<p>Linking a bank account lets Paylinq settle collected payments directly to you, and tag them for reconciliation.</p>
			</div>

			{accounts.length === 0 ? (
				<div className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-shadow-card">
					<EmptyState
						icon={Building2}
						title="No bank account linked"
						description="Add a bank account so your collected payments can be settled to you."
						action={
							<PermissionWrapper permission="add_bank_account">
								<CustomModal
									title="Add Bank Account"
									trigger={
										<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
											<Plus className="tw-h-4 tw-w-4" />
											Add Bank Account
										</Button>
									}
								>
									<NewAccountModal />
								</CustomModal>
							</PermissionWrapper>
						}
					/>
				</div>
			) : (
				<div className="tw-grid tw-gap-4 tw-grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
					{accounts.map((account) => (
						<AccountCard account={account} key={account.uuid} />
					))}

					<PermissionWrapper permission="add_bank_account">
						<CustomModal
							title="Add Bank Account"
							trigger={
								<button
									type="button"
									className="tw-flex tw-min-h-[168px] tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-border tw-bg-white/50 tw-p-5 tw-text-[13.5px] tw-font-semibold tw-text-text-soft tw-transition-colors hover:tw-border-teal hover:tw-text-teal"
								>
									<Plus className="tw-h-5 tw-w-5" />
									Add Another Bank
								</button>
							}
						>
							<NewAccountModal />
						</CustomModal>
					</PermissionWrapper>
				</div>
			)}
		</div>
	);
};

const AccountCard = ({ account }: { account: BankAccount }) => {
	const queryClient = useQueryClient();
	const canDisable = useHasPermissions("disable_bank_account");
	const canUpdate = useHasPermissions("update_bank_account");

	const { mutate: disableMutate, isLoading } = useMutation(disableAccount, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Account disabled");
			queryClient.invalidateQueries(["accounts"]);
		},
	});

	return (
		<div className="tw-flex tw-min-h-[168px] tw-flex-col tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-5 tw-shadow-card">
			<div className="tw-mb-4 tw-flex tw-items-start tw-justify-between tw-gap-2">
				<div className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-navy-tint tw-text-navy">
					<Building2 className="tw-h-4 tw-w-4" />
				</div>
				<div className="tw-flex tw-gap-1">
					{canUpdate && (
						<CustomModal
							title="Update Bank Account"
							trigger={
								<button
									type="button"
									aria-label="Update bank account"
									className="tw-rounded-lg tw-p-1.5 tw-text-text-soft tw-transition-colors hover:tw-bg-bg hover:tw-text-text"
								>
									<Pencil className="tw-h-3.5 tw-w-3.5" />
								</button>
							}
						>
							<NewAccountModal account={account} />
						</CustomModal>
					)}
					{canDisable && (
						<CustomConfirmDialog
							trigger={
								<button
									type="button"
									aria-label="Disable account"
									disabled={isLoading}
									className="tw-rounded-lg tw-p-1.5 tw-text-red tw-transition-colors hover:tw-bg-red-tint"
								>
									<Ban className="tw-h-3.5 tw-w-3.5" />
								</button>
							}
							mutateFn={() => disableMutate({ uuid: account.uuid })}
						/>
					)}
				</div>
			</div>

			<div className="tw-mb-1 tw-text-[12px] tw-text-text-soft">Bank Name</div>
			<div className="tw-mb-3 tw-text-[15px] tw-font-bold tw-text-text">{account.bank_name ?? "—"}</div>

			<div className="tw-mb-1 tw-text-[12px] tw-text-text-soft">Account Number</div>
			<div className="tw-text-[15px] tw-font-bold tw-text-text">{account.account_number ?? "—"}</div>

			{account.tag_name && (
				<div className="tw-mt-auto tw-pt-3">
					<span className="tw-inline-flex tw-rounded-full tw-bg-teal-tint tw-px-2.5 tw-py-1 tw-text-[11.5px] tw-font-semibold tw-text-teal-dark">
						{account.tag_name}
					</span>
				</div>
			)}
		</div>
	);
};

export default Accounts;
